// ./hooks/useMessage.js

import { useContext, useCallback, useEffect } from 'react';
import { GlobalContext } from '../context/provider';
import messageAPI from '../api/messageAPI';
import { messaging } from '../utilities/messaging/firebaseConfig';  // ✅ Add this
import { getToken } from 'firebase/messaging';  // ✅ Add this

const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;

const useMessage = () => {
  const { 
    messageState,
    messageDispatch
  } = useContext(GlobalContext);
  
  // ============================================================================
  // FCM TOKEN MANAGEMENT
  // ============================================================================

  const registerToken = useCallback(async (token) => {  // ✅ No userId parameter
    messageDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await messageAPI.registerToken(token);  // ✅ Only token
      if (response.success) {
        messageDispatch({ type: 'SET_USER_TOKEN', payload: { token } });  // ✅ Only token
      }
      return response;
    } catch (error) {
      messageDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [messageDispatch]);

  // Auto-register FCM token when user is authenticated
  const autoRegisterFCM = useCallback(async (user) => {
    if (!user || !user.id) return;

    const existingToken = sessionStorage.getItem('fcm_registered');
    if (existingToken) {
      console.log('✅ FCM already registered this session');
      return;
    }

    try {
      await navigator.serviceWorker.ready;
      const permission = await Notification.requestPermission();

      if (permission === 'granted') {
        const fcmToken = await getToken(messaging, { vapidKey });
        console.log('📱 FCM token received');

        const result = await registerToken(fcmToken);

        if (result.success) {
          console.log('✅ FCM token registered');
          sessionStorage.setItem('fcm_registered', 'true');
        }
      }
    } catch (error) {
      console.error('❌ FCM error:', error);
    }
  }, [registerToken]);

  const removeToken = useCallback(async (userId) => {
    messageDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await messageAPI.removeToken(userId);
      if (response.success) {
        messageDispatch({ type: 'REMOVE_USER_TOKEN' });
      }
      return response;
    } catch (error) {
      messageDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [messageDispatch]);

  const getRegisteredUsers = useCallback(async () => {
    messageDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await messageAPI.getRegisteredUsers();
      messageDispatch({ type: 'SET_REGISTERED_USERS', payload: response.users });
      return response;
    } catch (error) {
      messageDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [messageDispatch]);

  // ============================================================================
  // SEND MESSAGES
  // ============================================================================

  const sendMessage = useCallback(async (messageData) => {
    messageDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await messageAPI.sendMessage(messageData);
      if (response.success) {
        messageDispatch({ type: 'ADD_MESSAGE', payload: response });
      }
      return response;
    } catch (error) {
      messageDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [messageDispatch]);

  const sendMulticast = useCallback(async (adminId, adminName, title, body) => {
    messageDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await messageAPI.sendMulticast(adminId, adminName, title, body);
      if (response.success) {
        messageDispatch({ type: 'SET_MULTICAST_RESULT', payload: response });
      }
      return response;
    } catch (error) {
      messageDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [messageDispatch]);

  // ============================================================================
  // GET MESSAGES
  // ============================================================================

  const getMessagesAndMarkSeen = useCallback(async (senderId, receiverId, params = {}) => {
    messageDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await messageAPI.getMessagesAndMarkSeen(senderId, receiverId, params);
      messageDispatch({ type: 'SET_MESSAGES', payload: response });
      messageDispatch({ type: 'SET_CURRENT_CONVERSATION', payload: { senderId, receiverId } });
      return response;
    } catch (error) {
      messageDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [messageDispatch]);

  const getMessagesNoMarkSeen = useCallback(async (senderId, receiverId, params = {}) => {
    messageDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await messageAPI.getMessagesNoMarkSeen(senderId, receiverId, params);
      messageDispatch({ type: 'SET_MESSAGES', payload: response });
      return response;
    } catch (error) {
      messageDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [messageDispatch]);

  const getMessageInfo = useCallback(async (messageId) => {
    messageDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await messageAPI.getMessageInfo(messageId);
      messageDispatch({ type: 'SET_MESSAGE_INFO', payload: response.data });
      return response;
    } catch (error) {
      messageDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [messageDispatch]);

  // ============================================================================
  // UNREAD COUNTS
  // ============================================================================

  const getUnreadCount = useCallback(async (userId) => {
    messageDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await messageAPI.getUnreadCount(userId);
      messageDispatch({ type: 'SET_UNREAD_COUNT', payload: response.unreadCount });
      return response;
    } catch (error) {
      messageDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [messageDispatch]);

  const getUsersWithUnreadCounts = useCallback(async (receiverId, users) => {
    messageDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await messageAPI.getUsersWithUnreadCounts(receiverId, users);
      messageDispatch({ type: 'SET_USERS_WITH_UNREAD_COUNTS', payload: response });
      return response;
    } catch (error) {
      messageDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [messageDispatch]);

  // ============================================================================
  // MESSAGE STATUS
  // ============================================================================

  const markMessageAsRead = useCallback(async (messageId, userId) => {
    messageDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await messageAPI.markMessageAsRead(messageId, userId);
      if (response.success) {
        messageDispatch({ type: 'MARK_MESSAGE_AS_READ', payload: messageId });
        messageDispatch({ type: 'DECREMENT_UNREAD_COUNT', payload: 1 });
      }
      return response;
    } catch (error) {
      messageDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [messageDispatch]);

  const markAllAsRead = useCallback(async (userId) => {
    messageDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await messageAPI.markAllAsRead(userId);
      if (response.success) {
        messageDispatch({ type: 'MARK_ALL_AS_READ' });
      }
      return response;
    } catch (error) {
      messageDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [messageDispatch]);

  const updateMessageStatus = useCallback(async (messageId, status, userId) => {
    messageDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await messageAPI.updateMessageStatus(messageId, status, userId);
      if (response.success) {
        messageDispatch({ type: 'UPDATE_MESSAGE_STATUS', payload: { messageId, status } });
      }
      return response;
    } catch (error) {
      messageDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [messageDispatch]);

  const updateMultipleMessageStatus = useCallback(async (messageIds, status, userId) => {
    messageDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await messageAPI.updateMultipleMessageStatus(messageIds, status, userId);
      if (response.success) {
        messageDispatch({ type: 'UPDATE_MULTIPLE_MESSAGE_STATUS', payload: { messageIds, status } });
      }
      return response;
    } catch (error) {
      messageDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [messageDispatch]);

  // ============================================================================
  // DELETE MESSAGES
  // ============================================================================

  const deleteMessage = useCallback(async (messageId, userId) => {
    messageDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await messageAPI.deleteMessage(messageId, userId);
      if (response.success) {
        messageDispatch({ type: 'DELETE_MESSAGE', payload: messageId });
      }
      return response;
    } catch (error) {
      messageDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [messageDispatch]);

  // ============================================================================
  // ADMIN FUNCTIONS
  // ============================================================================

  const validateAllTokens = useCallback(async (adminId) => {
    messageDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await messageAPI.validateAllTokens(adminId);
      messageDispatch({ type: 'SET_VALIDATION_RESULTS', payload: response.results });
      return response;
    } catch (error) {
      messageDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [messageDispatch]);

  const testAllNotifications = useCallback(async (adminId, testMessage) => {
    messageDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await messageAPI.testAllNotifications(adminId, testMessage);
      messageDispatch({ type: 'SET_TEST_RESULTS', payload: response.results });
      return response;
    } catch (error) {
      messageDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [messageDispatch]);

  const getRegisteredTokensInfo = useCallback(async (adminId) => {
    messageDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await messageAPI.getRegisteredTokensInfo(adminId);
      messageDispatch({ type: 'SET_REGISTERED_TOKENS_INFO', payload: response });
      return response;
    } catch (error) {
      messageDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [messageDispatch]);

  const syncTokensFromDB = useCallback(async (adminId) => {
    messageDispatch({ type: 'SET_LOADING', payload: true });
    try {
      const response = await messageAPI.syncTokensFromDB(adminId);
      if (response.success) {
        messageDispatch({ type: 'SYNC_TOKENS_SUCCESS', payload: response });
      }
      return response;
    } catch (error) {
      messageDispatch({ type: 'SET_ERROR', payload: error.message });
      throw error;
    }
  }, [messageDispatch]);

  // ============================================================================
  // UTILITY
  // ============================================================================

  const clearMessageData = useCallback(() => {
    messageDispatch({ type: 'CLEAR_MESSAGE_DATA' });
  }, [messageDispatch]);

  const clearError = useCallback(() => {
    messageDispatch({ type: 'CLEAR_ERROR' });
  }, [messageDispatch]);

  return {
    // State
    messages: messageState?.messages,
    registeredUsers: messageState?.registeredUsers,
    userToken: messageState?.userToken,
    currentConversation: messageState?.currentConversation,
    messageInfo: messageState?.messageInfo,
    unreadCount: messageState?.unreadCount,
    usersWithUnreadCounts: messageState?.usersWithUnreadCounts,
    totalUnreadCount: messageState?.totalUnreadCount,
    countsBySender: messageState?.countsBySender,
    usersWithUnreadMessages: messageState?.usersWithUnreadMessages,
    totalMessages: messageState?.totalMessages,
    currentPage: messageState?.currentPage,
    totalPages: messageState?.totalPages,
    registeredTokensInfo: messageState?.registeredTokensInfo,
    validationResults: messageState?.validationResults,
    testResults: messageState?.testResults,
    lastMulticastResult: messageState?.lastMulticastResult,
    lastSentMessage: messageState?.lastSentMessage,
    isLoading: messageState?.isLoading,
    error: messageState?.error,

    // FCM Token Methods
    registerToken,
    autoRegisterFCM,
    removeToken,
    getRegisteredUsers,

    // Message Methods
    sendMessage,
    sendMulticast,
    getMessagesAndMarkSeen,
    getMessagesNoMarkSeen,
    getMessageInfo,

    // Unread Count Methods
    getUnreadCount,
    getUsersWithUnreadCounts,

    // Status Methods
    markMessageAsRead,
    markAllAsRead,
    updateMessageStatus,
    updateMultipleMessageStatus,

    // Delete Methods
    deleteMessage,

    // Admin Methods
    validateAllTokens,
    testAllNotifications,
    getRegisteredTokensInfo,
    syncTokensFromDB,

    // Utility Methods
    clearMessageData,
    clearError,
  };
};

export default useMessage;